import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, IconKebab, ContextMenu } from 'uikit';

import type { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { IRowWithCommonOptions } from '../types';

enum StateName {
    isFrozen = 'isFrozen',
    isEditInProgress = 'isEditInProgress',
}

type IMenuCellRenderer = CustomCellRendererProps<IRowWithCommonOptions> & {
    setValueState: (stateName: StateName, newState: boolean) => void;
    isKebabDisabled?: boolean;
};

export const MenuCellRenderer = (props: IMenuCellRenderer) => {
    const { api, data, setValueState, isKebabDisabled } = props;

    const ref = useRef(null);

    const [isContextOpened, setIsContextOpened] = useState(false);
    const toggleContextMenu = useCallback(() => {
        setIsContextOpened((s) => !s);
    }, []);

    const deleteItem = useCallback(() => {
        if (data) {
            api?.applyTransaction({
                remove: [data],
            });

            setValueState(StateName.isFrozen, false);
        }
    }, [api, data, setValueState]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: deleteItem,
        });

    const menuItems = useMemo(
        () => [
            {
                label: 'Редактировать',
                action: () => {
                    api?.applyTransaction({
                        update: [
                            {
                                ...data,
                                name: data?.name ?? '',
                                number: data?.number,
                                isEditing: true,
                            },
                        ],
                    });
                    setValueState(StateName.isEditInProgress, true);
                },
            },
            {
                label: 'Удалить',
                action: () => {
                    handleOpenConfirmationDialog();
                },
            },
        ],
        [api, data, handleOpenConfirmationDialog, setValueState],
    );

    const getItemOnClick = useCallback(
        (item: (typeof menuItems)[number]) => () => {
            item.action();
            toggleContextMenu();
        },
        [toggleContextMenu],
    );

    return (
        <>
            <Button
                ref={ref}
                iconLeft={IconKebab}
                view="clear"
                size="xs"
                onlyIcon
                onClick={toggleContextMenu}
                disabled={isKebabDisabled}
            />

            <ContextMenu
                isOpen={isContextOpened}
                items={menuItems}
                anchorRef={ref}
                direction="downStartRight"
                getItemOnClick={getItemOnClick}
                onClickOutside={toggleContextMenu}
            />

            <ConfirmationDialog
                textHeader="удаление записи"
                textConfirmation="Вы уверены, что хотите удалить запись? "
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

MenuCellRenderer.displayName = 'MenuCellRenderer';
